<template>
  <alert
    :message="updatePlanningStatus.message"
    :modalActive="modalActive"
    :isError="updatePlanningStatus.isError"
    @close="closeNotification"
  />
  <NewSchedule
    :isOpen="isShowNewSchedule"
    @close="createPlanFinish"
    :title="$t('createPlanning')"
    :startDate="startDate"
    :deviceCode="devicesStore.deviceData.code"
  />
  <ScheduleDetail
    :isOpen="isShowDetail"
    @close="detailClosed"
    :title="$t('planningDetail')"
    :id="planningId"
    :deviceCode="devicesStore.deviceData.code"
    :event="event"
  />
  <WeeklySchedule
    v-if="weeklyScheduleData"
    :isOpen="selectedWeek !== null"
    @close="closeWeeklySchedule"
    :title="weekTitle"
    :id="weeklyScheduleData.id"
    :deviceCode="weeklyScheduleData.deviceCode"
    :event="weeklyScheduleData.event"
  />
  <div class="planning-container">
    <sidebar :noSocial="true" :backOn="true" />
    <div class="device-container">
      <deviceCard :small="true" :content="newData[0]" :id="newData[0]?.id" />
      <div class="xs-icon-card">
        <img src="@/assets/report_planning.png" />
        <p>{{ $t("alarms") }}</p>
      </div>
    </div>
    <IdroTitle :title="title" />
    <div class="calendar-container">
      <!-- Custom Calendar Views -->
      <div
        v-if="
          currentView === 'dayGridWeek' ||
          currentView === 'dayGridMonth' ||
          currentView === 'timeGridDay'
        "
        class="custom-calendar"
      >
        <!-- Navigation Controls -->
        <div class="weekly-navigation">
          <button @click="previousMonth" class="nav-button">
            ← {{ $t("previous") }}
          </button>
          <div class="month-controls">
            <h2 class="month-title">
              {{
                currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })
              }}
            </h2>
            <div class="month-buttons"></div>
          </div>
          <button @click="nextMonth" class="nav-button">
            {{ $t("next") }} →
          </button>
        </div>

        <!-- View Navigation -->
        <div class="view-navigation">
          <!-- <span class="view-label">{{ $t("view") }}:</span> -->
          <!-- <button
            @click="switchToView('dayGridMonth')"
            :class="['view-button', { active: currentView === 'dayGridWeek' }]"
          >
            {{ $t("month") }}
          </button>
          <button
            @click="switchToView('dayGridWeek')"
            :class="['view-button', { active: currentView === 'dayGridWeek' }]"
          >
            {{ $t("week") }}
          </button>
          <button
            @click="switchToView('timeGridDay')"
            :class="['view-button', { active: currentView === 'dayGridWeek' }]"
          >
            {{ $t("day") }}
          </button> -->
        </div>

        <!-- Monthly View -->
        <div v-if="currentView === 'dayGridMonth'" class="monthly-view">
          <!-- Month Grid -->
          <div class="month-grid">
            <!-- Day Headers -->
            <div class="month-day-headers">
              <div
                v-for="day in dayHeaders"
                :key="day"
                class="month-day-header"
              >
                {{ day }}
              </div>
            </div>

            <!-- Month Days -->
            <div class="month-days">
              <div
                v-for="(day, dayIndex) in monthDays"
                :key="`month-day-${dayIndex}`"
                :class="[
                  'month-day-cell',
                  {
                    'other-month': !day.isCurrentMonth,
                    today: day.isToday,
                    'has-events': day.events.length > 0,
                  },
                ]"
                @click="handleDateClick(day)"
              >
                <div class="day-number">{{ day.date.getDate() }}</div>
                <div class="events-container">
                  <div
                    v-for="eventItem in day.events.slice(0, 2)"
                    :key="eventItem.id"
                    class="event-item"
                  >
                    {{ eventItem.programName }}
                  </div>
                  <div v-if="day.events.length > 2" class="more-events">
                    +{{ day.events.length - 2 }} {{ $t("more") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Weekly View -->
        <div v-else-if="currentView === 'dayGridWeek'" class="weekly-view">
          <!-- Week Overview -->
          <div class="weeks-overview">
            <div class="weeks-grid">
              <div
                v-for="(week, weekIndex) in calendarWeeks"
                :key="`week-${weekIndex}`"
                class="week-card"
                @click="openWeeklySchedule(weekIndex)"
              >
                <div class="week-number">
                  {{ $t("week") }} {{ weekIndex + 1 }}
                </div>
                <div class="week-dates">
                  {{ formatWeekDates(week) }}
                </div>
                <div class="week-events-count">
                  {{ getWeekEventsCount(week) }} program
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Daily View -->
        <div v-else-if="currentView === 'timeGridDay'" class="daily-view">
          <div class="daily-header">
            <div class="daily-navigation">
              <button @click="previousDay" class="nav-button">
                ← {{ $t("previousDay") }}
              </button>
              <h3>
                {{
                  currentDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                }}
              </h3>
              <button @click="nextDay" class="nav-button">
                {{ $t("nextDay") }} →
              </button>
            </div>
          </div>

          <div class="daily-timeline">
            <div v-for="hour in 24" :key="hour" class="hour-slot">
              <div class="hour-label">
                {{ (hour - 1).toString().padStart(2, "0") }}:00
              </div>
              <div class="hour-content">
                <div
                  v-for="event in getDayEvents(hour - 1)"
                  :key="event.id"
                  class="daily-event"
                  @click="handleEventClick(event)"
                >
                  {{ event.programName }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FullCalendar for fallback -->
      <FullCalendar v-else :options="calendarOptions" ref="calendar">
        <template #eventContent="{ event }">
          <b class="mx-2 cursor-pointer">{{
            event.extendedProps.programName
          }}</b>
        </template>
      </FullCalendar>
    </div>
  </div>
</template>

<script setup>
import FullCalendar from "@fullcalendar/vue3";
import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import NewSchedule from "@/components/modal/NewSchedule.vue";
import { useDevicesStore } from "@/stores/DevicesStore";
import { useI18n } from "vue-i18n";
import { usePlanningStore } from "@/stores/planning/PlanningStore";
import { storeToRefs } from "pinia";
import ScheduleDetail from "@/components/modal/ScheduleDetail.vue";
import WeeklySchedule from "@/components/modal/WeeklySchedule.vue";

//stores
const planningStore = usePlanningStore();
const { updatePlanningStatus, updatePlanningLoading } = storeToRefs(
  usePlanningStore()
);

const { t } = useI18n();
//props
const props = defineProps({
  id: String,
});

//asynchronus component
const deviceCard = defineAsyncComponent(() =>
  import("@/components/cards/deviceCardSmall.vue")
);

const devicesStore = useDevicesStore();
const title = ref();
const modalActive = ref(false);
const currentView = ref("dayGridWeek");
const currentDate = ref(new Date());
const selectedWeek = ref(null);

// Calendar data
const dayHeaders = computed(() => [
  t("sunday"),
  t("monday"),
  t("tuesday"),
  t("wednesday"),
  t("thursday"),
  t("friday"),
  t("saturday"),
]);
const eventList = ref([]);

const closeNotification = () => {
  modalActive.value = false;
};

const newData = computed(() => {
  return [devicesStore.deviceData];
});

function startOfWeek(date) {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  start.setHours(0, 0, 0, 0);
  return start;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(date.getDate() + days);
  return result;
}

function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isSameMonth(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth()
  );
}

// Generate calendar weeks
const calendarWeeks = computed(() => {
  const weeks = [];
  const monthStart = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth(),
    1
  );
  const monthEnd = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    0
  );
  let current = startOfWeek(monthStart);
  while (current <= monthEnd || weeks.length === 0) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(current);
      const dayEvents = eventList.value.filter((event) => {
        try {
          const eventDate = new Date(event.start);
          return isSameDay(dayDate, eventDate);
        } catch (error) {
          console.error("Error parsing event date:", event.start, error);
          return false;
        }
      });

      week.push({
        date: dayDate,
        isCurrentMonth: isSameMonth(dayDate, currentDate.value),
        isToday: isSameDay(dayDate, new Date()),
        events: dayEvents,
      });

      current = addDays(current, 1);
    }
    weeks.push(week);
    if (
      weeks.length >= 6 ||
      (weeks.length > 0 && current > addDays(monthEnd, 7))
    ) {
      break;
    }
  }
  return weeks;
});
const monthDays = computed(() => {
  const days = [];
  const monthStart = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth(),
    1
  );
  const monthEnd = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    0
  );
  let current = startOfWeek(monthStart);
  let weekCount = 0;
  while (weekCount < 6) {
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(current);
      const dayEvents = eventList.value.filter((event) => {
        try {
          const eventDate = new Date(event.start);
          return isSameDay(dayDate, eventDate);
        } catch (error) {
          console.error("Error parsing event date:", event.start, error);
          return false;
        }
      });

      days.push({
        date: dayDate,
        isCurrentMonth: isSameMonth(dayDate, currentDate.value),
        isToday: isSameDay(dayDate, new Date()),
        events: dayEvents,
      });

      current = addDays(current, 1);
    }
    weekCount++;
  }

  return days;
});

// Handle view changes from FullCalendar
function handleViewChange(info) {
  currentView.value = info.view.type;
  if (info.view.type === "dayGridWeek") {
    selectedWeek.value = null;
    currentDate.value = new Date(info.view.currentStart);
    if (eventList.value.length === 0) {
      fetchEventsAndUpdateCalendar();
    }
  } else {
    currentDate.value = new Date(info.view.currentStart);
  }
}

function handleFullCalendarDateClick(info) {
  if (currentView.value === "dayGridWeek") return;

  const clickedDate = new Date(info.dateStr).toISOString();
  const eventsInDate = eventList.value.filter((event) => {
    return event.start.startsWith(clickedDate);
  });

  isShowDetail.value = true;

  if (eventsInDate.length === 0) {
    event.value.existProgram = false;
  } else {
    planningId.value = eventsInDate[0].id;

    const startDate = new Date(eventsInDate[0].start);
    const startYear = startDate.getFullYear();
    const startMonth = String(startDate.getMonth() + 1).padStart(2, "0");
    const startDay = String(startDate.getDate()).padStart(2, "0");
    event.value.start = `${startYear}-${startMonth}-${startDay}`;

    const endDate = new Date(eventsInDate[0].end || eventsInDate[0].start);
    const endYear = endDate.getFullYear();
    const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
    const endDay = String(endDate.getDate()).padStart(2, "0");
    event.value.end = `${endYear}-${endMonth}-${endDay}`;

    event.value.programName = eventsInDate[0].programName;
    event.value.allProgram = eventsInDate;
    event.value.existProgram = true;
  }
}

function handleFullCalendarEventClick(arg) {
  if (currentView.value === "dayGridWeek") return;

  isShowDetail.value = true;
  planningId.value = arg.event.id;

  const startDate = new Date(arg.event.start);
  const startYear = startDate.getFullYear();
  const startMonth = String(startDate.getMonth() + 1).padStart(2, "0");
  const startDay = String(startDate.getDate()).padStart(2, "0");
  event.value.start = `${startYear}-${startMonth}-${startDay}`;

  const endDate = new Date(arg.event.end || arg.event.start);
  const endYear = endDate.getFullYear();
  const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
  const endDay = String(endDate.getDate()).padStart(2, "0");
  event.value.end = `${endYear}-${endMonth}-${endDay}`;

  event.value.programName = arg.event.extendedProps.programName;
  event.value.allProgram = planningStore.plannings.filter(
    (item) =>
      item.start === startDate.toISOString() &&
      item.end === endDate.toISOString()
  );
  event.value.existProgram = true;
}

// Helper functions for weekly view
function formatWeekDates(week) {
  if (!week || week.length === 0) return "";

  const firstDay = week[0].date;
  const lastDay = week[6].date;

  const firstFormatted = firstDay.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const lastFormatted = lastDay.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  return `${firstFormatted} - ${lastFormatted}`;
}

function getWeekEventsCount(week) {
  if (!week || week.length === 0) return 0;
  return week.reduce((total, day) => total + day.events.length, 0);
}

// Custom weekly calendar event handlers
function handleDateClick(day) {
  const clickedDate = day.date.toISOString();
  const eventsInDate = day.events;

  isShowDetail.value = true;

  // Log the data passed to the modal component
  console.log(
    "[Modal Data] planningId:",
    eventsInDate.length > 0 ? eventsInDate[0].id : null,
    "event:",
    {
      start:
        eventsInDate.length > 0
          ? (() => {
              const startDate = new Date(eventsInDate[0].start);
              const startYear = startDate.getFullYear();
              const startMonth = String(startDate.getMonth() + 1).padStart(
                2,
                "0"
              );
              const startDay = String(startDate.getDate()).padStart(2, "0");
              return `${startYear}-${startMonth}-${startDay}`;
            })()
          : day.date.toISOString().split("T")[0],
      end:
        eventsInDate.length > 0
          ? (() => {
              const endDate = new Date(
                eventsInDate[0].end || eventsInDate[0].start
              );
              const endYear = endDate.getFullYear();
              const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
              const endDay = String(endDate.getDate()).padStart(2, "0");
              return `${endYear}-${endMonth}-${endDay}`;
            })()
          : day.date.toISOString().split("T")[0],
      programName: eventsInDate.length > 0 ? eventsInDate[0].programName : null,
      allProgram: eventsInDate,
      existProgram: eventsInDate.length > 0,
    }
  );

  if (eventsInDate.length === 0) {
    event.value.existProgram = false;
    event.value.start = day.date.toISOString().split("T")[0];
    event.value.end = day.date.toISOString().split("T")[0];
  } else {
    planningId.value = eventsInDate[0].id;

    const startDate = new Date(eventsInDate[0].start);
    const startYear = startDate.getFullYear();
    const startMonth = String(startDate.getMonth() + 1).padStart(2, "0");
    const startDay = String(startDate.getDate()).padStart(2, "0");
    event.value.start = `${startYear}-${startMonth}-${startDay}`;

    const endDate = new Date(eventsInDate[0].end || eventsInDate[0].start);
    const endYear = endDate.getFullYear();
    const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
    const endDay = String(endDate.getDate()).padStart(2, "0");
    event.value.end = `${endYear}-${endMonth}-${endDay}`;

    event.value.programName = eventsInDate[0].programName;
    event.value.allProgram = eventsInDate;
    event.value.existProgram = true;
  }
}

function handleEventClick(eventItem) {
  isShowDetail.value = true;
  planningId.value = eventItem.id;

  const startDate = new Date(eventItem.start);
  const startYear = startDate.getFullYear();
  const startMonth = String(startDate.getMonth() + 1).padStart(2, "0");
  const startDay = String(startDate.getDate()).padStart(2, "0");
  event.value.start = `${startYear}-${startMonth}-${startDay}`;

  const endDate = new Date(eventItem.end || eventItem.start);
  const endYear = endDate.getFullYear();
  const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
  const endDay = String(endDate.getDate()).padStart(2, "0");
  event.value.end = `${endYear}-${endMonth}-${endDay}`;

  event.value.programName = eventItem.programName;
  event.value.existProgram = true;
}

// Navigation functions for weekly calendar
function previousMonth() {
  const newDate = new Date(currentDate.value);
  newDate.setMonth(newDate.getMonth() - 1);
  currentDate.value = newDate;
  selectedWeek.value = null;
}

function nextMonth() {
  const newDate = new Date(currentDate.value);
  newDate.setMonth(newDate.getMonth() + 1);
  currentDate.value = newDate;
  selectedWeek.value = null;
}

function goToToday() {
  currentDate.value = new Date();
  selectedWeek.value = null;
}

function previousDay() {
  const newDate = new Date(currentDate.value);
  newDate.setDate(newDate.getDate() - 1);
  currentDate.value = newDate;
}

function nextDay() {
  const newDate = new Date(currentDate.value);
  newDate.setDate(newDate.getDate() + 1);
  currentDate.value = newDate;
}

function refreshEvents() {
  fetchEventsAndUpdateCalendar();
}

function switchToView(viewType) {
  currentView.value = viewType;
  selectedWeek.value = null;
}

function getDayEvents(hour) {
  return eventList.value.filter((event) => {
    try {
      const eventDate = new Date(event.start);
      const eventHour = eventDate.getHours();
      const isSameDate = isSameDay(eventDate, currentDate.value);
      return isSameDate && eventHour === hour;
    } catch (error) {
      console.error("Error parsing event date:", event.start, error);
      return false;
    }
  });
}

onMounted(async () => {
  await devicesStore.loadDevice(props.id);
  await fetchEventsAndUpdateCalendar();
  title.value = "Idrosat:" + devicesStore.deviceData.name;
});

// Watch for view changes
watch(currentView, (newView) => {
  if (newView !== "dayGridWeek") {
    selectedWeek.value = null;
  }
});

// Watch for selected week changes
watch(selectedWeek, (newWeek) => {
  // Week selection changed
});

// Watch for event list changes to update weekly calendar
watch(
  eventList,
  (newEvents) => {
    if (currentView.value === "dayGridWeek") {
      // Trigger a reactive update by accessing the computed property
    }
  },
  { deep: true }
);

// Watch for current date changes to update weekly calendar
watch(currentDate, (newDate) => {
  if (currentView.value === "dayGridWeek") {
    // Weekly calendar should update for new month
  }
});

const calendar = ref(null);

const calendarOptions = {
  plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
  initialView: "dayGridWeek",
  events: [],
  headerToolbar: {
    start: "dayGridWeek,dayGridMonth,timeGridDay",
    center: "title",
  },
  dayMaxEvents: false,
  dateClick: handleFullCalendarDateClick,
  eventClick: handleFullCalendarEventClick,
  datesSet: handleViewChange,
};

async function fetchEventsAndUpdateCalendar() {
  await planningStore.getPlannings({
    deviceCode: devicesStore.deviceData.code,
  });
  eventList.value = planningStore.plannings;

  if (calendar.value) {
    const api = calendar.value.getApi();
    api.removeAllEvents();
    api.addEventSource(planningStore.plannings);
  }
  if (currentView.value === "dayGridWeek") {
  }
}

function createPlanFinish() {
  isShowNewSchedule.value = false;
  fetchEventsAndUpdateCalendar();
}

function detailClosed() {
  isShowDetail.value = false;
  fetchEventsAndUpdateCalendar();
}

// Modal state
const startDate = ref("");
const isShowNewSchedule = ref(false);
const isShowDetail = ref(false);
const planningId = ref("");
const event = ref({
  start: null,
  end: null,
  programName: null,
  existProgram: false,
});

// Computed for WeeklySchedule modal
const weekTitle = computed(() => {
  if (selectedWeek.value === null) return "";
  const week = calendarWeeks.value[selectedWeek.value];
  if (!week) return "";
  const firstDay = week[0].date;
  const lastDay = week[6].date;
  const firstFormatted = firstDay.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const lastFormatted = lastDay.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return `${t("week")} ${
    selectedWeek.value + 1
  } - ${firstFormatted} ~ ${lastFormatted}`;
});

const weekEvents = computed(() => {
  if (selectedWeek.value === null) return [];
  const week = calendarWeeks.value[selectedWeek.value];
  if (!week) return [];
  return week.flatMap((day) => day.events);
});

function openWeeklySchedule(weekIndex) {
  selectedWeek.value = weekIndex;
}
function closeWeeklySchedule() {
  selectedWeek.value = null;
}

// Computed for WeeklySchedule props (same structure as ScheduleDetail)
const weeklyScheduleData = computed(() => {
  if (selectedWeek.value === null) return null;
  const week = calendarWeeks.value[selectedWeek.value];
  if (!week) return null;
  const allWeekEvents = week.flatMap((day) => day.events);

  if (allWeekEvents.length === 0) {
    // No programs for this week
    const firstDay = week[0].date;
    const lastDay = week[6].date;
    console.log(
      "[weeklyScheduleData] No events found, returning empty structure"
    );
    return {
      id: null,
      deviceCode: devicesStore.deviceData.code,
      event: {
        start: firstDay.toISOString().split("T")[0],
        end: lastDay.toISOString().split("T")[0],
        programName: null,
        allProgram: [],
        existProgram: false,
      },
    };
  }

  const firstEvent = allWeekEvents[0];
  const startDate = new Date(firstEvent.start);
  const startYear = startDate.getFullYear();
  const startMonth = String(startDate.getMonth() + 1).padStart(2, "0");
  const startDay = String(startDate.getDate()).padStart(2, "0");
  const formattedStart = `${startYear}-${startMonth}-${startDay}`;

  const endDate = new Date(firstEvent.end || firstEvent.start);
  const endYear = endDate.getFullYear();
  const endMonth = String(endDate.getMonth() + 1).padStart(2, "0");
  const endDay = String(endDate.getDate()).padStart(2, "0");
  const formattedEnd = `${endYear}-${endMonth}-${endDay}`;

  const result = {
    id: firstEvent.id,
    deviceCode: devicesStore.deviceData.code,
    event: {
      start: formattedStart,
      end: formattedEnd,
      programName: firstEvent.programName,
      allProgram: allWeekEvents,
      existProgram: true,
    },
  };
  return result;
});
</script>

<style scoped>
.planning-container {
  @apply relative flex flex-col px-[16px] md:px-[200px] lg:px-[260px] xl:px-[320px] 2xl:px-[360px];
}

.device-container {
  @apply flex sm:flex-col fixed items-end sm:items-center gap-2 bottom-0 left-4 pb-4 sm:pb-8;
}

.calendar-container {
  @apply bg-white p-4 rounded-lg border;
}

/* Custom Calendar Styles */
.custom-calendar {
  @apply space-y-4;
}

.weekly-calendar {
  @apply space-y-4;
}

/* Monthly View Styles */
.monthly-view {
  @apply space-y-4;
}

.month-grid {
  @apply space-y-2;
}

.month-day-headers {
  @apply grid grid-cols-7 gap-1 mb-2;
}

.month-day-header {
  @apply text-center font-semibold text-gray-600 py-2 bg-gray-100 rounded-md;
}

.month-days {
  @apply grid grid-cols-7 gap-1;
}

.month-day-cell {
  @apply min-h-[120px] border border-gray-200 rounded-md p-2 cursor-pointer transition-colors duration-200 hover:bg-gray-50;
}

.month-day-cell.other-month {
  @apply opacity-40 bg-gray-50;
}

.month-day-cell.today {
  @apply bg-blue-100 border-blue-300;
}

.month-day-cell.has-events {
  @apply border-blue-400;
}

/* Daily View Styles */
.daily-view {
  @apply space-y-4;
}

.daily-header {
  @apply mb-4;
}

.daily-navigation {
  @apply flex items-center justify-between mb-4;
}

.daily-navigation h3 {
  @apply text-xl font-semibold text-gray-800;
}

.daily-header h3 {
  @apply text-xl font-semibold text-gray-800;
}

.daily-timeline {
  @apply space-y-1;
}

.hour-slot {
  @apply flex border-b border-gray-200 min-h-[60px];
}

.hour-label {
  @apply w-16 text-sm text-gray-500 font-medium py-2 px-2 border-r border-gray-200;
}

.hour-content {
  @apply flex-1 p-2;
}

.daily-event {
  @apply bg-blue-500 text-white px-2 py-1 rounded text-sm mb-1 cursor-pointer hover:bg-blue-600 transition-colors duration-200;
}

/* Weekly View Styles */
.weekly-view {
  @apply space-y-4;
}

.weekly-navigation {
  @apply flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg;
}

.nav-button {
  @apply px-4 py-2 text-white rounded-md transition-colors duration-200;
  background-color: rgb(26, 37, 47);
}

.nav-button:hover {
  background-color: rgb(36, 47, 57);
}

.month-controls {
  @apply flex flex-col items-center space-y-2;
}

.month-title {
  @apply text-xl font-semibold text-gray-800;
}

.month-buttons {
  @apply flex space-x-2;
}

.today-button {
  @apply px-3 py-1 text-white rounded-md transition-colors duration-200 text-sm;
  background-color: rgb(26, 37, 47);
}

.today-button:hover {
  background-color: rgb(36, 47, 57);
}

.refresh-button {
  @apply px-3 py-1 text-white rounded-md transition-colors duration-200 text-sm;
  background-color: rgb(26, 37, 47);
}

.refresh-button:hover {
  background-color: rgb(36, 47, 57);
}

.view-navigation {
  @apply flex justify-center items-center space-x-2 mt-4;
}

.view-label {
  @apply text-sm font-medium text-gray-600;
}

.view-button {
  @apply px-4 py-2 text-gray-700 rounded-md transition-colors duration-200 font-medium;
  background-color: rgb(240, 240, 240);
}

.view-button:hover {
  background-color: rgb(220, 220, 220);
}

.view-button.active {
  @apply text-white;
  background-color: rgb(26, 37, 47);
}

.view-button.active:hover {
  background-color: rgb(36, 47, 57);
}

/* Week Overview Styles */
.weeks-overview {
  @apply space-y-4;
}

.debug-info {
  @apply bg-yellow-100 p-3 rounded-lg border border-yellow-300 text-sm;
}

.debug-info p {
  @apply mb-1;
}

.weeks-grid {
  @apply grid grid-cols-1 gap-4;
}

.week-card {
  @apply bg-white border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-300;
}

.week-card:hover {
  @apply transform -translate-y-1;
}

.week-number {
  @apply text-lg font-semibold text-gray-800 mb-2;
}

.week-dates {
  @apply text-sm text-gray-600 mb-2;
}

.week-events-count {
  @apply text-xs text-blue-600 font-medium;
}

/* Selected Week Detail Styles */
.selected-week-detail {
  @apply space-y-4;
}

.week-header {
  @apply flex items-center justify-between mb-4;
}

.back-button {
  @apply px-4 py-2 text-white rounded-md transition-colors duration-200;
  background-color: rgb(26, 37, 47);
}

.back-button:hover {
  background-color: rgb(36, 47, 57);
}

.week-header h3 {
  @apply text-xl font-semibold text-gray-800;
}

.day-headers {
  @apply grid grid-cols-7 gap-1 mb-4;
}

.day-header {
  @apply text-center font-semibold text-gray-600 py-2 bg-gray-100 rounded-md;
}

.week-row {
  @apply grid grid-cols-7 gap-1;
}

.day-cell {
  @apply min-h-[100px] border border-gray-200 rounded-md p-2 cursor-pointer transition-colors duration-200 hover:bg-gray-50;
}

.day-cell.other-month {
  @apply opacity-40 bg-gray-50;
}

.day-cell.today {
  @apply bg-blue-100 border-blue-300;
}

.day-cell.has-events {
  @apply border-blue-400;
}

.day-number {
  @apply font-semibold text-gray-700 mb-1;
}

.events-container {
  @apply space-y-1;
}

.event-item {
  @apply text-xs bg-blue-500 text-white px-2 py-1 rounded truncate cursor-pointer;
}

.more-events {
  @apply text-xs text-gray-600 font-medium;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .day-cell {
    @apply min-h-[80px] p-1;
  }

  .week-label {
    @apply text-xs px-3 py-1;
  }

  .day-header {
    @apply text-sm py-1;
  }

  .weeks-grid {
    @apply grid-cols-1;
  }
}

/* Weekly View with Program Details Styles */
.week-days-details {
  @apply grid grid-cols-1 gap-4;
}

.day-detail-card {
  @apply bg-white border border-gray-200 rounded-lg p-4 shadow-sm;
}

.day-detail-header {
  @apply flex items-center justify-between mb-4 pb-3 border-b border-gray-200;
}

.day-info {
  @apply flex items-center gap-3;
}

.day-name {
  @apply text-lg font-semibold text-gray-800;
}

.day-date {
  @apply text-sm text-gray-600;
}

.today-badge {
  @apply px-2 py-1 text-xs font-medium text-white rounded-full;
  background-color: rgb(26, 37, 47);
}

.day-events-count {
  @apply text-sm text-gray-600 font-medium;
}

.programs-list {
  @apply space-y-3;
}

.no-programs {
  @apply text-center py-6;
}

.no-programs-text {
  @apply text-gray-500 text-sm italic;
}

.programs-count {
  @apply flex justify-center;
}

.count-badge {
  @apply px-4 py-2 text-white rounded-full text-sm font-medium cursor-pointer transition-all duration-200 hover:shadow-md;
  background-color: rgb(26, 37, 47);
}

.count-badge:hover {
  background-color: rgb(36, 47, 57);
}

/* Responsive adjustments for weekly view */
@media (max-width: 768px) {
  .week-days-details {
    @apply grid-cols-1;
  }

  .day-detail-card {
    @apply p-3;
  }

  .day-detail-header {
    @apply flex-col items-start gap-2;
  }
}

.fc {
  @apply text-[8px] sm:text-xs lg:text-base;
}
</style>
